<template>
  <div>
    <!-- Precio de venta editable -->
    <v-row align="center" class="mb-1">
      <!-- Campo de precio -->
      <v-col cols="12" sm="8">
        <GenericAttributeInput
          attributeName="precioVenta"
          :label="precioVentaLabel"
          type="string"
          :value="precioVenta"
          :rules="`required|decimal:2|greater_than_cost:${precioCosto}`"
          :outlined="outlined"
          :disabled="!esPrecioVentaManual || disabledCalculado"
          @update-attribute="handleGenericInputChange"
          @click:append-outer="clickAppendOuter"
          ref="genericInput"
        />
        <!-- Precio base visual mejorada -->
        <v-sheet
          v-if="showImpuestosInfo"
          class=""
          elevation="0"
          rounded
          color="transparent"
          style="border: 1px solid #ccc"
        >
          <div class="d-flex align-center">
            <v-icon small class="mr-2" color="grey lighten-1"
              >mdi-cash-minus</v-icon
            >
            <div class="caption grey--text text--lighten-1">
              Precio sin impuestos nacionales:
              <span class="font-weight-medium">{{
                precioBase | convertPesoArg
              }}</span>
            </div>
          </div>
        </v-sheet>
      </v-col>

      <!-- Bloque de precio sugerido -->
      <v-col
        cols="12"
        sm="4"
        v-if="esPrecioVentaManual && !esPrecioIgualAlCalculado && showPrecioSugerencia"
        class="d-flex flex-column justify-center"
      >
        <v-card flat class="elevation-0 pa-3 border-alert">
          <v-container class="pa-0">
            <div class="d-flex align-center mb-2">
              <v-icon color="warning" small class="mr-2"
                >mdi-alert-circle</v-icon
              >
              <div
                class="text-subtitle-2 font-weight-medium amber--text text--darken-2"
              >
                El precio de venta difiere del cálculo automático.
              </div>
            </div>

            <div class="d-flex align-center justify-space-between mb-1">
              <div>
                <div class="caption grey--text text--darken-1 mb-1">
                  Precio sugerido (costo + margen + IVA):
                </div>
                <div class="text-h6 font-weight-bold primary--text">
                  {{ calculatedSellingPrice | convertPesoArg }}
                </div>
              </div>
              <v-btn
                small
                color="primary"
                outlined
                @click="actualizarPrecioVenta"
              >
                Corregir precio
              </v-btn>
            </div>
          </v-container>
        </v-card>
      </v-col>
    </v-row>

    <!-- Diálogo por cambio de IVA -->
    <v-dialog v-model="dialogCambioIva" persistent max-width="520">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">
          Se actualizó el IVA del producto
        </v-card-title>

        <v-card-text>
          <p>
            El valor del IVA fue modificado. ¿Desea actualizar el precio de
            venta?
          </p>

          <v-row class="my-4" align="center" justify="space-around">
            <v-col cols="12" sm="6" class="text-center">
              <div class="text-caption">Precio actual</div>
              <div class="text-h6 font-weight-bold grey--text text--darken-2">
                ${{ precioVenta }}
              </div>
            </v-col>

            <v-col cols="12" sm="6" class="text-center">
              <div class="text-caption">Nuevo precio sugerido</div>
              <div class="text-h6 font-weight-bold green--text">
                ${{ calculatedSellingPrice }}
              </div>
            </v-col>
          </v-row>

          <p class="text-caption grey--text text--darken-1">
            El nuevo precio fue calculado automáticamente para mantener el
            margen de ganancia establecido.
          </p>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="justify-end">
          <v-btn text @click="mantenerPrecioDeVenta">
            Mantener precio actual
          </v-btn>
          <v-btn color="primary" @click="actualizarPrecioVenta">
            Aplicar nuevo precio
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>



<script>
import GenericAttributeInput from "@/components/util/GenericAttributeInput.vue";

// La regla personalizada 'greater_than_cost' debe estar disponible donde se use el wrapper (en el padre).

export default {
  name: "PrecioVentaCalculatorWrapper",
  inject: ["$validator"],

  components: {
    GenericAttributeInput,
  },

  props: {
    precioCosto: { type: String, default: 0 },
    precioBase: { type: String, default: 0 },

    precioVenta: { type: String, default: null }, // Valor manual actual recibido del padre
    ganancia: { type: String, default: 0 },
    flete: { type: Number, default: 0 },
    mantenerGanancia: { type: Boolean, default: false },
    iva: {
      type: Object,
      default: () => ({ description: "0" }),
      validator: (obj) => obj == null || obj.hasOwnProperty("description"),
    },

    esPrecioVentaManual: { type: Boolean, default: false },
    tipoRedondeo: {
      type: String,
      default: "decimales",
      validator: (value) => ["decimales", "entero"].includes(value),
    },
    outlined: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    showImpuestosInfo: { type: Boolean, default: true },
    showPrecioSugerencia: { type: Boolean, default: true },

    // Agrega otras props pasadas al genérico si las usas (ej: appendOuterIcon, iconButton)
  },
  data() {
    return {
      dialogCambioIva: false,
      oldIvaVenta: null,
      lastKnownIvaDescription: null,
    };
  },

  computed: {
    esPrecioIgualAlCalculado() {
      const actual = parseFloat(this.precioVenta).toFixed(2);
      const sugerido = parseFloat(this.calculatedSellingPrice).toFixed(2);
      console.log(
        `Comparando precios: Actual: ${actual}, Sugerido: ${sugerido}`
      );

      return actual === sugerido;
    },
    disabledCalculado() {
      return this.disabled;
    },

    precioVentaLabel() {
      return this.esPrecioVentaManual
        ? "Precio de venta"
        : "Precio de venta calculado automáticamente";
    },

    /**
     * Calcula el precio de venta automático basado en las props.
     * Devuelve null si los inputs no son suficientes.
     */

    calculatedGain() {
      const precioCosto = parseFloat(this.precioCosto).toFixed(2) || 0;
      const precioVentaManual = parseFloat(this.precioVenta) || 0; // Usa la prop precioVenta
      const fletePorc = parseFloat(this.flete) / 100 || 0;
      const ivaVentaPorc = parseFloat(this.iva?.description) / 100 || 0;

      const costoTotal = precioCosto * (1 + fletePorc);

      // Validar si se puede calcular la ganancia
      if (costoTotal <= 0 || precioVentaManual <= 0) {
        return 0; // O null
      }

      const precioVentaSinIVA = precioVentaManual / (1 + ivaVentaPorc);
      const ganancia = (precioVentaSinIVA / costoTotal - 1) * 100;

      return parseFloat(ganancia.toFixed(2));
    },

    /**
     * Calcula la ganancia porcentual basada en el precio de venta manual.
     * Devuelve 0 o null si no se puede calcular.
     */
    calculatedSellingPrice() {
      return this.calcularPrecioVenta();
    },
    // Siempre mostramos la sugerencia con decimales

    /**
     * Reglas de validación para el input genérico (precioVenta).
     */
  },

  methods: {
    redondear(valor, tipo) {
      if (tipo === "entero") {
        return Math.round(valor);
      } else if (tipo === "decimales") {
        return parseFloat(Number(valor).toFixed(2));
      }
      return valor;
    },
    calcularPrecioVenta() {
      const costo = parseFloat(this.precioCosto) || 0;
      const ganancia = parseFloat(this.ganancia) / 100 || 0;
      const flete = parseFloat(this.flete) / 100 || 0;
      const iva = parseFloat(this.iva?.description) / 100 || 0;
      console.log(
        `Inputs para calcular PV: Costo: ${costo}, Ganancia: ${ganancia}, Flete: ${flete}, IVA: ${iva}`
      );

      if (costo <= 0 || ganancia < 0) return null;

      const bruto = costo * (1 + ganancia) * (1 + flete) * (1 + iva);
      console.log(`Precio bruto calculado: ${bruto}`);

      console.log(
        `Calculando precio de venta: Costo: ${costo}, Ganancia: ${ganancia}, Flete: ${flete}, IVA: ${iva}, Bruto: ${bruto}`
      );
      console.log(
        `Redondeando precio bruto: ${bruto} con tipo ${this.tipoRedondeo}`
      );

      return this.redondear(bruto, this.tipoRedondeo).toFixed(2);
    },

    actualizarPrecioVenta() {
      this.$emit("update:precioVenta", this.calcularPrecioVenta());
      console.log(this.calcularPrecioVenta());
      
      this.dialogCambioIva = false;
    },
    mantenerPrecioDeVenta() {
      this.recalculateAndEmitGainIfManual();

      this.dialogCambioIva = false;
    },
    recalculateAndEmitGainIfManual() {
      if (this.esPrecioVentaManual) {
        console.log(
          "Wrapper (Manual Mode Watch): Inputs de cálculo de ganancia cambiaron. Recalculando y emitiendo ganancia."
        );
        // calculatedGain usa la prop precioVenta (el valor manual actual).
        const nuevaGanancia =
          this.calculatedGain != null
            ? Number(this.calculatedGain).toFixed(2)
            : "0.00";
        console.log(nuevaGanancia);

        this.$emit("update:ganancia", nuevaGanancia);
      }
    },
    /**
     * Maneja el evento 'update:value' del GenericValidatedInput.
     * Este método **SOLO se llama** cuando el usuario edita el input,
     * lo cual solo es posible si `disabledCalculado` es false (modo manual).
     */
    handleGenericInputChange(newValue) {
      // Cuando el usuario edita en modo manual, el nuevo valor editado (newValue)
      // es el nuevo precio de venta manual. Debemos emitirlo al padre.
      console.log(
        "Wrapper (Manual Edit): Recibido newValue de GenericInput ->",
        newValue
      );

      // 1. Emitir el nuevo precio de venta manual
      this.$emit("update:precioVenta", newValue.value);

      // 2. Recalcular la ganancia basada en el nuevo PV manual y emitirla.
      // calculatedGain usa la prop 'precioVenta'. Como acabamos de emitir 'update:precioVenta',
      // el padre recibirá esto, actualizará su estado, y esa actualización volverá
      // a este wrapper como la prop 'precioVenta'. El computed 'calculatedGain' reaccionará.
      // Emitir la ganancia aquí es un poco adelantado, pero común. Podrías emitir
      // en el watcher de 'precioVenta' si prefieres que la prop se actualice primero.
      // Para simplificar, emitimos aquí usando el valor del input para el cálculo.

      //  console.log("Wrapper (Manual Edit): Emitiendo update:ganancia con ->", this.calculatedGain);
      //  this.$emit('update:ganancia', this.calculatedGain);
    },

    // Pasa el evento clickAppendOuter hacia arriba
    clickAppendOuter() {
      this.$emit("click-button-append");
    },

    // Método para que el padre pueda llamar a la validación de este wrapper
    async validate() {
      // Llama al método validate del componente genérico interno usando la ref
      const isValid = await this.$refs.genericInput.validate();
      return isValid;
    },
  },

  watch: {
    /**
     * Watcher para emitir el precio de venta CALCULADO al padre
     * cada vez que cambie, PERO SOLO EN MODO AUTOMÁTICO.
     */
    calculatedSellingPrice(newValue, oldValue) {
      // Revisa si estamos en modo automático Y si el nuevo valor calculado es diferente al viejo
      // Y si el nuevo valor calculado no es null (si no se pudo calcular, quizás no emitir o emitir null)

      if (!this.esPrecioVentaManual && newValue !== oldValue) {
        console.log(
          "Wrapper (Auto Mode Watch): Precio calculado cambió a ->",
          newValue
        );
        // Emitir el nuevo valor calculado al padre
        this.$emit("update:precioVenta", String(newValue)); // Emite el PV calculado
      }
    },

    /**
     * Watcher para emitir la ganancia calculada al padre
     * cuando los inputs de cálculo de GANANCIA cambien, PERO SOLO EN MODO MANUAL.
     * Esto cubre los casos donde precioCosto, flete, o iva cambian mientras se edita el PV manualmente.
     */
    precioCosto(newVal, oldVal) {
      this.recalculateAndEmitGainIfManual();
    },
    iva: {
      handler(newVal) {
        const newDesc = parseFloat(newVal?.description) || 0;

        if (this.lastKnownIvaDescription === null) {
          this.lastKnownIvaDescription = newDesc;
          return;
        }

        if (this.esPrecioVentaManual && newDesc !== this.lastKnownIvaDescription) {
          this.oldIvaVenta = { description: String(this.lastKnownIvaDescription) };
          this.dialogCambioIva = true;
        }

        this.lastKnownIvaDescription = newDesc;
      },
      immediate: true,
    },

    precioVenta(newValue, oldValue) {
      console.log(
        `Wrapper Watcher: Prop 'precioVenta' cambió de ${oldValue} a ${newValue}.`
      );
      // Llama al método auxiliar para recalcular y emitir la ganancia,
      // el cual verificará si estamos en modo manual antes de emitir.
      this.recalculateAndEmitGainIfManual();
      // Nota: handleGenericInputChange ya emite ganancia al editar manualmente.
      // Este watcher se disparará *después* de que el padre actualice la prop
      // basado en ese emit. Si solo quieres emitir ganancia *una vez* por cambio,
      // podrías remover la emisión de ganancia de handleGenericInputChange
      // y dejar que solo este watcher lo haga. Pero emitir en ambos sitios
      // (inmediatamente al editar y después de la propagación de la prop)
      // suele ser seguro si el padre maneja bien las actualizaciones.
    },

    // No necesitamos watcher directo en 'ganancia' aquí para este propósito

    // Watcher en esPrecioVentaManual para manejar transiciones entre modos
    esPrecioVentaManual(newMode, oldMode) {
      console.log(
        `Wrapper: Modo cambiado de ${oldMode ? "Manual" : "Automático"} a ${
          newMode ? "Manual" : "Automático"
        }`
      );
      // Si cambiamos A MODO AUTOMÁTICO, disparamos una emisión del valor calculado actual
      // (El watcher de calculatedSellingPrice con immediate: true o una llamada manual aquí lo haría)
      if (!newMode) {
        // Cambiando a Automático
        console.log(
          "Wrapper: Transición a Modo Automático. Emitiendo valor calculado actual."
        );
        // Emitir el valor calculado actual al padre.
        this.$emit("update:precioVenta", this.calculatedSellingPrice);
        // No emitimos ganancia, ya que en modo auto la ganancia no se edita ni se muestra en el campo de input.
      } else {
        // Cambiando a Manual
        console.log("Wrapper: Transición a Modo Manual.");
        // Podrías querer recalcular y emitir la ganancia si el PV manual ya tiene un valor
        this.$options.methods.recalculateAndEmitGainIfManual.call(this);
      }
    },
  },

  // Puedes añadir el mounted para validación inicial
  mounted() {
    console.log("Wrapper Mounted.");
    // Validar el estado inicial del input genérico
    if (this.displayValue != null && this.displayValue !== "") {
      this.$refs.genericInput.validate();
    }

    // Emitir el valor inicial (calculado o manual) al padre al montar
    // Esto asegura que el padre tenga el valor correcto desde el principio.
    // El watcher de calculatedSellingPrice con immediate: true ya haría esto en modo auto.
    // Para cubrir el modo manual inicial también, emitimos explícitamente:
    console.log(
      "Wrapper Mounted: Emitiendo valor inicial ->",
      this.displayValue
    );
    // this.$emit('update:precioVenta', this.displayValue);

    // Si estás en modo manual al montar y quieres que la ganancia se emita inicialmente
    //  if (this.esPrecioVentaManual && this.precioVenta != null) {
    //   console.log("HELLO");

    //       this.$options.methods.recalculateAndEmitGainIfManual.call(this);
    //  }
  },
};
</script>

<style scoped>
/* Estilos */
</style>